import { useState } from 'react';
import { StatusIcon } from './StatusIcon';
import { formatDurationMs } from '@/lib/format';
import type { DagNodeState } from '@/lib/types';

interface DagNodeProgressProps {
  nodes: DagNodeState[];
  activeNodeId: string | null;
  onNodeClick: (nodeId: string) => void;
}

function DagNodeItem({
  node,
  isActive,
  onNodeClick,
}: {
  node: DagNodeState;
  isActive: boolean;
  onNodeClick: (nodeId: string) => void;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const hasIterations = (node.iterations?.length ?? 0) > 0;

  return (
    <div>
      <div
        className={`w-full cursor-pointer rounded-xl border px-3 py-2 text-left transition-colors ${
          isActive
            ? 'border-accent/40 bg-accent/10 shadow-sm'
            : 'border-transparent hover:border-border/60 hover:bg-surface-hover'
        }`}
        onClick={(): void => {
          onNodeClick(node.nodeId);
        }}
        role="row"
      >
        <div className="flex items-center gap-2 text-sm">
          {hasIterations && (
            <button
              type="button"
              onClick={(e): void => {
                e.stopPropagation();
                setExpanded(prev => !prev);
              }}
              className="shrink-0 cursor-pointer text-xs text-text-tertiary hover:text-text-secondary"
              aria-label={expanded ? 'Collapse iterations' : 'Expand iterations'}
            >
              {expanded ? '\u25BC' : '\u25B6'}
            </button>
          )}
          <StatusIcon status={node.status} />
          <span className="flex-1 truncate font-medium">{node.name}</span>
          {node.currentIteration !== undefined && node.maxIterations !== undefined && (
            <span className="shrink-0 rounded-full border border-border/60 bg-background/80 px-2 py-0.5 text-xs text-text-secondary">
              {node.currentIteration}/{node.maxIterations}
            </span>
          )}
          {node.duration !== undefined && (
            <span className="shrink-0 rounded-full border border-border/60 bg-background/80 px-2 py-0.5 text-xs text-text-secondary">
              {formatDurationMs(node.duration)}
            </span>
          )}
          {node.costUsd !== undefined && node.costUsd > 0 && (
            <span className="shrink-0 rounded-full border border-border/60 bg-background/80 px-2 py-0.5 text-xs text-text-secondary">
              ${node.costUsd.toFixed(4)}
            </span>
          )}
        </div>
        {node.error && (
          <div className="ml-6 mt-1 truncate text-xs text-error" title={node.error}>
            {node.error.slice(0, 80)}
          </div>
        )}
        {node.reason && (
          <div className="ml-6 mt-1 text-xs text-text-tertiary">
            Skipped: {node.reason.replace(/_/g, ' ')}
          </div>
        )}
      </div>
      {expanded && hasIterations && (
        <div className="ml-6 mt-1 space-y-1">
          {(node.iterations ?? []).map(iter => (
            <div
              key={iter.iteration}
              className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/60 px-2 py-1 text-xs"
            >
              <StatusIcon status={iter.status} />
              <span className="text-text-secondary flex-1">Iteration {iter.iteration}</span>
              {iter.duration !== undefined && (
                <span className="text-text-tertiary">{formatDurationMs(iter.duration)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DagNodeProgress({
  nodes,
  activeNodeId,
  onNodeClick,
}: DagNodeProgressProps): React.ReactElement {
  if (nodes.length === 0) {
    return (
      <div className="p-3 text-xs text-text-secondary italic">No DAG node events recorded.</div>
    );
  }

  return (
    <div className="space-y-2 p-2">
      {nodes.map(node => (
        <DagNodeItem
          key={node.nodeId}
          node={node}
          isActive={node.nodeId === activeNodeId}
          onNodeClick={onNodeClick}
        />
      ))}
    </div>
  );
}
